import { useMemo, useState } from "react";

import { useNotification } from "../../../hooks";
import { useInspectionAttributes } from "../../../hooks/query";
import type { EstimatingFormState } from "../types";
import {
    type EstimatingAttributeChange,
    identifyEstimatingAttributeChanges,
} from "../utils/estimatingAttributes";

type UseEstimatingSyncParams = {
    projectId?: string;
    current: EstimatingFormState;
    saved: EstimatingFormState;
    attributeIds: Record<string, string>;
    enabled?: boolean;
    onSaved: (
        next: EstimatingFormState,
        attributeIds: Record<string, string>,
    ) => void;
};

export default function useEstimatingSync({
    projectId,
    current,
    saved,
    attributeIds,
    enabled = true,
    onSaved,
}: UseEstimatingSyncParams) {
    const [syncing, setSyncing] = useState(false);
    const { mutateAsync: createAttribute } =
        useInspectionAttributes().useCreate();
    const { mutateAsync: updateAttribute } =
        useInspectionAttributes().useUpdateSingleAttribute();
    const { showSuccess, showError } = useNotification();

    const changeIdentified = useMemo(() => {
        if (!enabled) return false;
        return identifyEstimatingAttributeChanges(current, saved).length > 0;
    }, [current, saved, enabled]);

    async function persistChanges(changes: EstimatingAttributeChange[]) {
        if (!projectId) {
            return;
        }

        const nextAttributeIds = { ...attributeIds };
        const nextSaved = { ...saved };

        for (const change of changes) {
            const existingId = nextAttributeIds[change.attributeName];

            if (existingId) {
                await updateAttribute({
                    Id: existingId,
                    Value: change.value,
                });
            } else {
                const created: any = await createAttribute({
                    ProjectId: projectId,
                    AttributeName: change.attributeName,
                    Value: change.value,
                });

                if (created?.Id) {
                    nextAttributeIds[change.attributeName] = created.Id;
                }
            }

            Object.assign(nextSaved, {
                [change.field]: current[change.field],
            });
        }

        onSaved(nextSaved, nextAttributeIds);
        showSuccess("Estimating settings saved");
    }

    async function flushSave() {
        if (!projectId || !enabled) {
            return { success: false };
        }

        const changes = identifyEstimatingAttributeChanges(current, saved);
        if (changes.length === 0) {
            return { success: true };
        }

        setSyncing(true);

        try {
            await persistChanges(changes);
            return { success: true };
        } catch (err) {
            console.error("[EstimateContract] estimating sync failed", err);
            showError("Failed to save estimating settings");
            return { success: false };
        } finally {
            setSyncing(false);
        }
    }

    return {
        changeIdentified,
        flushSave,
        syncing,
    };
}
